"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { Heart, ArrowLeft, MapPin } from "lucide-react"
import { ref, onValue, remove } from "../lib/firebase-database"
import { db } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import LoadingAnimation from "./LoadingAnimation"
import { UserCircleIcon, Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/solid"
import LoginPopup from "./login-popup"
import { MobileBottomNav } from "../components/MobileBottomNav"
import AvatarCircle from "./AvatarCircle"
import Logo from "./Logo"
import { MobileMenu } from "./MobileMenu"

export default function WishlistsPage() {
  const [wishlistedVillas, setWishlistedVillas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user, isAdmin, isOwner } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showLoginPopup, setShowLoginPopup] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)

  // Add this useEffect at the beginning of the component, right after the state declarations
  useEffect(() => {
    if (!user) {
      // If user is not logged in, redirect to home and show login popup
      navigate("/", { state: { from: location, showLogin: true } })
    }
  }, [user, navigate, location])

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  useEffect(() => {
    if (user) {
      const wishlistRef = ref(db, `wishlists/${user.uid}`)
      onValue(wishlistRef, async (snapshot) => {
        const wishlistData = snapshot.val()
        if (wishlistData) {
          const villaPromises = Object.keys(wishlistData).map(
            (villaId) =>
              new Promise((resolve) => {
                const villaRef = ref(db, `villas/${villaId}`)
                onValue(
                  villaRef,
                  (villaSnapshot) => {
                    const villaData = villaSnapshot.val()
                    if (villaData) {
                      resolve({ id: villaId, ...villaData })
                    } else {
                      resolve(null)
                    }
                  },
                  {
                    onlyOnce: true,
                  },
                )
              }),
          )
          const villas = await Promise.all(villaPromises)
          setWishlistedVillas(villas.filter((villa) => villa !== null))
        } else {
          setWishlistedVillas([])
        }
        setLoading(false)
      })
    }
  }, [user])

  const removeFromWishlist = async (villaId) => {
    try {
      await remove(ref(db, `wishlists/${user.uid}/${villaId}`))
      setWishlistedVillas((prevVillas) => prevVillas.filter((villa) => villa.id !== villaId))
    } catch (error) {
      console.error("Error removing villa from wishlist:", error)
      setError("Failed to remove villa from wishlist. Please try again.")
    }
  }

  const goToProfile = () => {
    navigate("/profile")
  }

  const goToAdminDashboard = () => {
    navigate("/admin")
    setShowProfileMenu(false)
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true)
    setShowProfileMenu(false)
  }

  const handleBack = () => {
    navigate(-1)
  }

  return (
    <div className="min-h-screen bg-white">
      {isMobile ? (
        <>
          {/* Mobile Header */}
          <div className="sticky top-0 bg-white z-50">
            <div className="flex items-center px-4 py-3">
              <button onClick={handleBack} className="flex items-center text-gray-800">
                <ArrowLeft className="h-6 w-6 mr-2" />
              </button>
              <h1 className="text-xl font-semibold ml-2">Saved</h1>
            </div>
          </div>

          <main className="pb-16">
            {loading ? (
              <LoadingAnimation />
            ) : error ? (
              <div className="text-center text-red-600 p-4">{error}</div>
            ) : wishlistedVillas.length === 0 ? (
              <div className="text-center p-8">
                <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl font-semibold">No saved villas yet</p>
                <p className="mt-2 text-gray-600">Start exploring and save villas you like!</p>
                <Link
                  to="/"
                  className="mt-4 inline-block bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition duration-300"
                >
                  Explore Villas
                </Link>
              </div>
            ) : (
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2">
                  {wishlistedVillas.map((villa) => (
                    <Link
                      to={`/villa/${villa.id}`}
                      key={villa.id}
                      className="flex-shrink-0 w-full bg-white rounded-[20px] overflow-hidden relative h-[240px]"
                    >
                      <div className="relative w-full h-full">
                        <img
                          src={villa.thumbnail || villa.images[0]}
                          alt={villa.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = "https://source.unsplash.com/300x200/?villa"
                          }}
                        />
                        {/* Gradient overlay */}
                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/70 to-transparent" />

                        {/* Wishlist button */}
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            removeFromWishlist(villa.id)
                          }}
                          className="absolute top-4 right-4 z-10 bg-white p-1.5 rounded-full shadow-md hover:scale-110 transition-transform"
                        >
                          <Heart className="h-5 w-5 text-red-500 fill-current" />
                        </button>

                        {/* Villa Details */}
                        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                          <h3 className="text-base font-bold line-clamp-2 mb-1">{villa.name}</h3>
                          <div className="flex items-center opacity-100 mb-1">
                            <span className="text-sm">{villa.mainCity}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <p className="text-base font-bold">
                              ₹{villa.rate}
                              <span className="text-sm font-normal opacity-90">/night</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </main>

          <MobileBottomNav user={user} activeTab="wishlists" />
        </>
      ) : (
        <>
          {/* Header */}
          <header className="bg-black text-white p-4 sticky top-0 z-50">
            <div className="container mx-auto px-4 flex items-center justify-between">
              {isMobile ? (
                <>
                  <Bars3Icon className="h-6 w-6 cursor-pointer" onClick={toggleMobileMenu} />
                  <Logo />
                  {user ? (
                    <button onClick={goToProfile}>
                      <AvatarCircle
                        src={user.photoURL}
                        name={user.displayName || user.email}
                        className="h-8 w-8"
                        textClassName="text-xs"
                      />
                    </button>
                  ) : (
                    <UserCircleIcon className="h-6 w-6" onClick={() => setShowLoginPopup(true)} />
                  )}
                </>
              ) : (
                <>
                  <Logo />
                  <nav className="flex items-center space-x-4">
                    <Link
                      to="/for-owner"
                      className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      For Owners
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    {isOwner && (
                      <Link
                        to="/owner"
                        className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Your Villa
                      </Link>
                    )}
                    <Link
                      to="/wishlists"
                      className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Wishlists
                    </Link>
                    {user ? (
                      <div className="relative">
                        <button
                          onClick={() => setShowProfileMenu(!showProfileMenu)}
                          className="flex items-center space-x-2 hover:bg-gray-700 px-3 py-2 rounded transition duration-300"
                        >
                          <AvatarCircle
                            src={user.photoURL}
                            name={user.displayName || user.email}
                            className="w-8 h-8"
                            textClassName="text-xs"
                          />
                          <ChevronDownIcon className="h-4 w-4" />
                        </button>
                        {showProfileMenu && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 text-gray-700">
                            <div className="px-4 py-2 border-b">
                              <p className="text-sm font-medium">Hi, {user.displayName}</p>
                            </div>
                            <button
                              onClick={goToProfile}
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            >
                              View Profile
                            </button>
                            {isOwner && (
                              <Link
                                to="/owner"
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                onClick={() => setShowProfileMenu(false)}
                              >
                                Owner Dashboard
                              </Link>
                            )}
                            {isAdmin && (
                              <button
                                onClick={goToAdminDashboard}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              >
                                Admin Dashboard
                              </button>
                            )}
                            <button
                              onClick={handleLogoutClick}
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
                            >
                              Logout
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowLoginPopup(true)}
                        className="hover:bg-gray-700 px-3 py-2 rounded transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Login
                      </button>
                    )}
                  </nav>
                </>
              )}
            </div>
          </header>
          <MobileMenu
            isOpen={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            user={user}
            isOwner={isOwner}
            isAdmin={isAdmin}
            onLogout={handleLogoutClick}
            toggleLoginPopup={setShowLoginPopup}
          />

          {/* Add sticky heading section for mobile */}
          {isMobile && (
            <div className="sticky top-[62px] z-10 bg-white border-b">
              <div className="flex items-center justify-center py-6">
                <h1 className="text-2xl font-bold text-center text-black">Wishlists</h1>
              </div>
            </div>
          )}

          <main className="container mx-auto px-4 py-8">
            {loading && <LoadingAnimation />}
            {error ? (
              <div className="text-center text-red-600">{error}</div>
            ) : wishlistedVillas.length === 0 ? (
              <div className="text-center">
                <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl font-semibold">No wishlisted villas yet</p>
                <p className="mt-2 text-gray-600">Start exploring and add villas to your wishlist!</p>
                <Link
                  to="/"
                  className="mt-4 inline-block bg-black text-white px-6 py-2 rounded-full hover:bg-gray-800 transition duration-300"
                >
                  Explore Villas
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {wishlistedVillas.map((villa) => (
                  <div key={villa.id} className="bg-white rounded-lg shadow-md overflow-hidden sm:flex-col sm:h-auto">
                    <div className="flex flex-row sm:flex-col h-full">
                      <div className="relative w-1/2 sm:w-full h-40 sm:h-48 p-3">
                        <img
                          src={villa.images[0] || "/placeholder.svg"}
                          alt={villa.name}
                          className="absolute inset-0 w-full h-full object-cover rounded-lg"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = "https://source.unsplash.com/300x200/?villa"
                          }}
                        />
                        <div className="absolute top-2 right-2">
                          <button
                            onClick={() => removeFromWishlist(villa.id)}
                            className="text-white hover:text-red-500 transition duration-300"
                          >
                            <Heart className="h-6 w-6 fill-current" />
                          </button>
                        </div>
                      </div>
                      <div className="p-4 flex-1 flex flex-col justify-between min-h-[160px] sm:min-h-0">
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <h3 className="text-base sm:text-lg font-semibold line-clamp-1">{villa.name}</h3>
                              <p className="text-xs sm:text-sm text-gray-600 flex items-center">
                                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                                <span className="line-clamp-1">{villa.address}</span>
                              </p>
                              <p className="font-bold text-sm sm:text-base">₹{villa.rate} per Night</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end mt-2">
                          <Link
                            to={`/villa/${villa.id}`}
                            className="bg-black text-white px-4 py-1.5 rounded-full text-xs sm:text-sm hover:bg-gray-800 transition duration-300"
                          >
                            View
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </>
      )}

      {isMobile && <MobileBottomNav user={user} activeTab="wishlists" />}
      {showLoginPopup && <LoginPopup onClose={() => { setShowLoginPopup(false); navigate("/", { replace: true }); }} />}
    </div>
  )
}
